import * as THREE from "three";
import { SceneManager } from "../core/scene_manager.js";

export const RIM_MODES = [ "dir", "hemi", "ambient", "off" ] as const;
export type RimMode = typeof RIM_MODES[number];

export const RIM_COLORS: { name: string; hex: number }[] = [
  { name: "ice", hex: 0x88bbff },
  { name: "white", hex: 0xffffff },
  { name: "warm", hex: 0xffd9a0 },
  { name: "violet", hex: 0xb08cff },
  { name: "teal", hex: 0x7fffe4 },
];

const SUBSTITUTE_NAME = "rim_substitute";
const GROUND_TINT = 0x2a3f6b;

export class RimLightTest
{
  private static mode: RimMode = "dir";
  private static color = 0x88bbff;
  private static intensity = 1;
  private static substitute: THREE.Light | null = null;

  public static setMode( mode: RimMode ): void
  {
    this.mode = mode;
    this.apply();
  }

  public static setColor( hex: number ): void
  {
    this.color = hex;
    this.apply();
  }

  public static setIntensity( value: number ): void
  {
    this.intensity = value;
    this.apply();
  }

  public static cycleMode(): RimMode
  {
    const next = ( RIM_MODES.indexOf( this.mode ) + 1 ) % RIM_MODES.length;
    this.setMode( RIM_MODES[next] );
    return this.mode;
  }

  public static cycleColor(): string
  {
    const current = RIM_COLORS.findIndex( entry => entry.hex === this.color );
    const next = RIM_COLORS[( current + 1 ) % RIM_COLORS.length];
    this.setColor( next.hex );
    return next.name;
  }

  public static getState(): {
    mode: RimMode;
    color: string;
    intensity: number;
    dir_visible: boolean;
    substitute: string | null;
    directionals: number;
  }
  {
    const rim = this.findRim();
    let directionals = 0;

    SceneManager.world.traverse( object =>
    {
      const light = object as THREE.DirectionalLight;
      if ( ( light as { isDirectionalLight?: boolean } ).isDirectionalLight && light.visible )
        directionals++;
    } );

    return {
      mode: this.mode,
      color: `#${this.color.toString( 16 ).padStart( 6, "0" )}`,
      intensity: this.intensity,
      dir_visible: rim?.visible ?? false,
      substitute: this.substitute ? this.substitute.type : null,
      directionals,
    };
  }

  public static modeLabel(): string
  {
    const name = RIM_COLORS.find( entry => entry.hex === this.color )?.name ?? "custom";
    return `${this.mode.toUpperCase()} / ${name}`;
  }

  public static reapply(): void
  {
    if ( this.substitute && this.substitute.parent === null )
      this.substitute = null;

    this.apply();
  }

  public static reset(): void
  {
    this.mode = "dir";
    this.color = 0x88bbff;
    this.intensity = 1;
    this.apply();
  }

  private static findRim(): THREE.DirectionalLight | null
  {
    let found: THREE.DirectionalLight | null = null;

    SceneManager.world.traverse( object =>
    {
      if ( object.name === "rim" )
        found = object as THREE.DirectionalLight;
    } );

    return found;
  }

  private static clearSubstitute(): void
  {
    if ( !this.substitute )
      return;

    this.substitute.removeFromParent();
    this.substitute.dispose?.();
    this.substitute = null;
  }

  private static apply(): void
  {
    const rim = this.findRim();
    if ( rim )
      rim.visible = this.mode === "dir";

    if ( rim && this.mode === "dir" )
    {
      rim.color.setHex( this.color );
      rim.intensity = this.intensity;
    }

    const wanted = this.mode === "hemi"
      ? "HemisphereLight"
      : this.mode === "ambient"
        ? "AmbientLight"
        : null;

    if ( this.substitute && this.substitute.type !== wanted )
      this.clearSubstitute();

    if ( wanted === null )
      return;

    if ( !this.substitute )
    {
      this.substitute = wanted === "HemisphereLight"
        ? new THREE.HemisphereLight( this.color, GROUND_TINT, this.intensity )
        : new THREE.AmbientLight( this.color, this.intensity );

      this.substitute.name = SUBSTITUTE_NAME;
      SceneManager.world.add( this.substitute );
    }

    this.substitute.color.setHex( this.color );
    this.substitute.intensity = this.intensity;
  }
}
